import { Box, Container, Flex, Heading, Image, Spinner, Stack, Text } from '@chakra-ui/react';
import { useMemo, useState } from 'react';

import { useListContracts, useListServices } from 'hooks/useApiQueries';
import {
  mergePricingContract,
  mergePricingService,
  mergeTopBlock,
} from 'screens/Prices/utils/mergePricingService';
import type { MindbodyContract } from 'types/contract';
import type { Service } from 'types/service';
import type {
  PricingCategory,
  PricingConfig,
  PricingContract,
  PricingContractsColumns,
  PricingGlobalTexts,
  PricingSubTab,
  PricingTopBlocks,
} from 'types/pricing';

import { ContractCard } from './ContractCard';
import { AlmaMention } from './AlmaMention';
import { ExclusivityBanner } from './ExclusivityBanner';
import { PackCard } from './PackCard';
import { SegmentedControl } from './SegmentedControl';
import { StudioOffersBlock } from './StudioOffersBlock';
import { TopFeatureCard } from './TopFeatureCard';
import { getBrandColor } from 'utils/brandColor';

/**
 * Largeur maximale réelle d'une carte : imposée par `.serviceCard`
 * (`max-width: 328px` dans index.css, héritage de l'ancienne page Tarifs).
 * Les colonnes des grilles ci-dessous sont exprimées en pourcentage du
 * container et peuvent donc être bien plus larges que ça — jusqu'à 572px pour
 * 2 colonnes sur un container de 1200px. Sans borne sur la grille, chaque
 * carte se colle à gauche de sa colonne et l'ensemble paraît décalé vers la
 * gauche au lieu d'être centré sous les onglets.
 */
const CARD_MAX_WIDTH = 328;

/** Gap desktop des grilles (`gap={{ md: 6 }}` = 24px). */
const GRID_GAP = 24;

/** Largeur occupée par `n` cartes côte à côte, gaps compris. */
function gridMaxWidth(n: number): string {
  return `${n * CARD_MAX_WIDTH + (n - 1) * GRID_GAP}px`;
}

/**
 * New customer-facing Pricing page rendered when the WP admin toggle
 * "Activer la nouvelle page Tarifs" is ON.
 *
 * Two layout modes driven by `config.layout`:
 * - **flat**: all categories + all contracts in sequential sections (legacy
 *   parity, no navigation). This is the default.
 * - **navigation**: top-level segmented control PACKS / ABONNEMENTS, then a
 *   row of sub-tabs filtering the visible categories or contracts. Each
 *   sub-tab can carry its own color theme that propagates to its cards.
 *
 * Common to both modes: page title → top blocks → … → studio offers →
 * exclusivity banner.
 */
export function ServicesContracts() {
  const config = (typeof mondayMindbody !== 'undefined' && mondayMindbody?.pricingConfig) as PricingConfig | undefined;
  const { data: liveServices, isLoading, isError } = useListServices();

  const hasContracts = (config?.contracts.length ?? 0) > 0;
  const { data: liveContracts } = useListContracts(
    hasContracts ? config?.defaultLocationId ?? 1 : undefined,
  );

  const liveById = useMemo(() => {
    const map = new Map<string, Service>();
    (liveServices ?? []).forEach((svc) => {
      if (svc?.Id !== undefined && svc?.Id !== null) {
        map.set(String(svc.Id), svc);
      }
    });
    return map;
  }, [liveServices]);

  const liveContractById = useMemo(() => {
    const map = new Map<string, MindbodyContract>();
    (liveContracts ?? []).forEach((c) => {
      if (c?.Id !== undefined && c?.Id !== null) {
        map.set(String(c.Id), c);
      }
    });
    return map;
  }, [liveContracts]);

  if (!config) return null;

  if (isLoading) {
    return (
      <Container maxW="1200px" py={10}>
        <Stack align="center" spacing={4}>
          <Spinner color="orange.500" size="lg" />
          <Text color="gray.500">Chargement des tarifs…</Text>
        </Stack>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container maxW="1200px" py={10}>
        <Text color="red.500" textAlign="center">
          Impossible de charger les tarifs pour le moment.
        </Text>
      </Container>
    );
  }

  const orderedCategories = [...config.categories].sort((a, b) => a.sortOrder - b.sortOrder);
  const globalTexts = config.globalTexts;
  const almaText = globalTexts?.almaCardText ?? '';

  return (
    <Container maxW="1200px" py={{ base: 6, md: 10 }} className="ServicesPage ServicesPage--v2">
      <Heading
        as="h1"
        textAlign="center"
        color="orange.500"
        fontSize={{ base: '2xl', md: '4xl' }}
        mb={{ base: 6, md: 10 }}
        className="ServicesTitle"
      >
        {config.pageTitle || 'Tarifs'}
        {/* Sous-titre rendu DANS le titre, comme l'ancien
            `.ServicesTitle:after { content: "…" }` du CSS personnalisé qu'il
            remplace : il hérite de la police et de la graisse du titre, et
            garde la marge du titre en dessous de lui. Les styles propres
            reprennent ceux du pseudo-élément (bloc, 1rem, noir, casse libre). */}
        {config.pageSubtitle?.trim() && (
          <Box
            as="span"
            display="block"
            className="ServicesSubtitle"
            fontSize="1rem"
            textTransform="none"
            color="black"
          >
            {config.pageSubtitle}
          </Box>
        )}
      </Heading>

      {config.topBlocks.items.length > 0 && (
        <TopBlocksRow
          topBlocks={config.topBlocks}
          liveById={liveById}
          globalTexts={globalTexts}
          almaText={almaText}
        />
      )}

      {config.layout === 'navigation' ? (
        <NavigationLayout
          config={config}
          orderedCategories={orderedCategories}
          liveById={liveById}
          liveContractById={liveContractById}
          globalTexts={globalTexts}
          almaText={almaText}
        />
      ) : (
        <FlatLayout
          orderedCategories={orderedCategories}
          contracts={config.contracts}
          hasContracts={hasContracts}
          liveById={liveById}
          liveContractById={liveContractById}
          globalTexts={globalTexts}
          almaText={almaText}
          abonnementsLabel={config.segments?.abonnements?.label ?? 'Abonnements'}
          studioOffers={config.studioOffers}
          contractsColumns={config.contractsColumns}
        />
      )}

      <ExclusivityBanner banner={config.exclusivityBanner} />
    </Container>
  );
}

// ---------------------------------------------------------------------------
// FLAT LAYOUT — legacy-equivalent: all categories then all contracts
// ---------------------------------------------------------------------------

interface FlatLayoutProps {
  orderedCategories: PricingCategory[];
  contracts: PricingContract[];
  hasContracts: boolean;
  liveById: Map<string, Service>;
  liveContractById: Map<string, MindbodyContract>;
  globalTexts: PricingGlobalTexts;
  almaText: string;
  abonnementsLabel: string;
  studioOffers: PricingConfig['studioOffers'];
  contractsColumns: PricingContractsColumns;
}

function FlatLayout({
  orderedCategories,
  contracts,
  hasContracts,
  liveById,
  liveContractById,
  globalTexts,
  almaText,
  abonnementsLabel,
  studioOffers,
  contractsColumns,
}: FlatLayoutProps) {
  return (
    <>
      <Stack spacing={{ base: 8, md: 12 }}>
        {orderedCategories.map((category) => (
          <CategoryBlock
            key={category.id}
            category={category}
            liveById={liveById}
            globalTexts={globalTexts}
            almaText={almaText}
          />
        ))}
      </Stack>

      <StudioOffersBlock block={studioOffers} />

      {hasContracts && (
        <ContractsBlock
          contracts={contracts}
          liveContractById={liveContractById}
          liveById={liveById}
          globalTexts={globalTexts}
          heading={abonnementsLabel}
          columns={contractsColumns}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// NAVIGATION LAYOUT — segmented control + sub-tabs filtering
// ---------------------------------------------------------------------------

interface NavigationLayoutProps {
  config: PricingConfig;
  orderedCategories: PricingCategory[];
  liveById: Map<string, Service>;
  liveContractById: Map<string, MindbodyContract>;
  globalTexts: PricingGlobalTexts;
  almaText: string;
}

function NavigationLayout({
  config,
  orderedCategories,
  liveById,
  liveContractById,
  globalTexts,
  almaText,
}: NavigationLayoutProps) {
  // Brand primary color (BO Mindbody plugin setting), used as the default
  // active color for both the top segmented control and any sub-tab that
  // doesn't define its own color.
  const primaryColor =
    getBrandColor();

  // Which top segment is active. We default to the first segment that has
  // any content (avoids landing on an empty PACKS page if the admin only
  // configured ABONNEMENTS).
  const packsHasItems = orderedCategories.length > 0;
  const abosHasItems = config.contracts.length > 0;
  const initialSegment: 'packs' | 'abonnements' = packsHasItems ? 'packs' : 'abonnements';
  const [activeSegment, setActiveSegment] = useState<'packs' | 'abonnements'>(initialSegment);

  // If only one segment has content, hide the segmented control entirely.
  const showSegmentedControl = packsHasItems && abosHasItems;

  const segments = config.segments;
  const segmentOptions = [
    { id: 'packs', label: segments.packs?.label || 'PACKS', activeColor: segments.packs?.color || primaryColor, sortOrder: segments.packs?.sortOrder ?? 0 },
    { id: 'abonnements', label: segments.abonnements?.label || 'ABONNEMENTS', activeColor: segments.abonnements?.color || primaryColor, sortOrder: segments.abonnements?.sortOrder ?? 1 },
  ].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <Box>
      {showSegmentedControl && (
        <Flex justify="center" mb={4}>
          <SegmentedControl
            options={segmentOptions}
            activeId={activeSegment}
            onChange={(id) => setActiveSegment(id as 'packs' | 'abonnements')}
            defaultActiveColor={primaryColor}
            size="lg"
          />
        </Flex>
      )}

      {activeSegment === 'packs' ? (
        <SegmentBody
          intro={segments.packs?.introText ?? ''}
          subTabs={segments.packs?.subTabs ?? []}
          primaryColor={primaryColor}
          // No sub-tabs → render all categories flat. With sub-tabs → render
          // only the categories linked to the active sub-tab.
          renderActiveContent={(activeSubTab) => {
            const visibleCategories = activeSubTab
              ? orderedCategories.filter((cat) => activeSubTab.categoryIds?.includes(cat.id))
              : orderedCategories;
            return (
              <Stack spacing={{ base: 8, md: 12 }}>
                {visibleCategories.map((category) => (
                  <CategoryBlock
                    key={category.id}
                    category={category}
                    liveById={liveById}
                    globalTexts={globalTexts}
                    almaText={almaText}
                    hideHeader
                  />
                ))}
                <StudioOffersBlock block={config.studioOffers} />
              </Stack>
            );
          }}
        />
      ) : (
        <SegmentBody
          intro={segments.abonnements?.introText ?? ''}
          subTabs={segments.abonnements?.subTabs ?? []}
          primaryColor={primaryColor}
          renderActiveContent={(activeSubTab) => {
            const visibleContracts = activeSubTab
              ? config.contracts.filter((c) => activeSubTab.contractIds?.includes(c.id))
              : config.contracts;
            return (
              <ContractsBlock
                contracts={visibleContracts}
                liveContractById={liveContractById}
                liveById={liveById}
                globalTexts={globalTexts}
                columns={config.contractsColumns}
                // No heading inside the segment body — the segmented
                // control already labels the section.
              />
            );
          }}
        />
      )}
    </Box>
  );
}

interface SegmentBodyProps {
  intro: string;
  subTabs: PricingSubTab[];
  primaryColor: string;
  renderActiveContent: (activeSubTab: PricingSubTab | null) => React.ReactNode;
}

/**
 * Renders the body of one segment: optional intro text, optional sub-tab
 * row, then the content delegated to `renderActiveContent`. If there are
 * no sub-tabs, `activeSubTab` is null and the caller renders everything.
 */
function SegmentBody({ intro, subTabs, primaryColor, renderActiveContent }: SegmentBodyProps) {
  const orderedSubTabs = useMemo(() => [...subTabs].sort((a, b) => a.sortOrder - b.sortOrder), [subTabs]);
  const [activeSubTabId, setActiveSubTabId] = useState<string | null>(orderedSubTabs[0]?.id ?? null);

  // If sub-tabs change (admin edit + auto-save returns), make sure the
  // active id is still valid.
  const activeSubTab = orderedSubTabs.find((s) => s.id === activeSubTabId) ?? orderedSubTabs[0] ?? null;

  return (
    <Box>
      {intro && (
        <Text textAlign="center" fontSize="sm" color="gray.700" maxW="700px" mx="auto" mb={5}>
          {intro}
        </Text>
      )}

      {orderedSubTabs.length > 0 && (
        <Flex justify="center" mb={6}>
          <SegmentedControl
            options={orderedSubTabs.map((st) => ({
              id: st.id,
              label: st.label,
              activeColor: st.color || primaryColor,
            }))}
            activeId={activeSubTab?.id ?? ''}
            onChange={(id) => setActiveSubTabId(id)}
            defaultActiveColor={primaryColor}
            size="md"
          />
        </Flex>
      )}

      {activeSubTab?.introText && (
        <Text textAlign="center" fontSize="sm" color="gray.600" maxW="700px" mx="auto" mb={5}>
          {activeSubTab.introText}
        </Text>
      )}

      {renderActiveContent(orderedSubTabs.length > 0 ? activeSubTab : null)}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// SHARED BLOCKS — used by both layouts
// ---------------------------------------------------------------------------

interface TopBlocksRowProps {
  topBlocks: PricingTopBlocks;
  liveById: Map<string, Service>;
  globalTexts: PricingGlobalTexts;
  almaText: string;
}

function TopBlocksRow({ topBlocks, liveById, globalTexts, almaText }: TopBlocksRowProps) {
  const merged = useMemo(() => {
    return [...topBlocks.items]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((b) => mergeTopBlock(b, globalTexts, b.mindbodyId ? liveById.get(String(b.mindbodyId)) : undefined))
      .filter((m): m is NonNullable<typeof m> => m !== null);
  }, [topBlocks.items, globalTexts, liveById]);

  if (merged.length === 0) return null;

  const centralImageUrl = topBlocks.centralImage?.url;
  const showCentralImage = !!centralImageUrl && merged.length === 2;

  const borderRadius = `${Number(mondayMindbody?.customization?.cardBorderRadius) || 12}px`;

  return (
    <Box mb={{ base: 8, md: 12 }} className="topBlocksRow">
      {showCentralImage ? (
        /* Grid: card | image | card — all stretch to the same height */
        <Flex
          justify="center"
          align="stretch"
          gap={{ base: 4, md: 5 }}
          flexWrap="wrap"
          className="topBlocksGrid topBlocksGrid--withImage"
        >
          <TopFeatureCard merged={merged[0]} almaText={almaText} />
          <Box
            display={{ base: 'none', md: 'block' }}
            w="320px"
            borderRadius={borderRadius}
            overflow="hidden"
            flexShrink={0}
            className="topBlocksCentralImage"
          >
            <Image src={centralImageUrl} alt="" w="full" h="full" objectFit="cover" />
          </Box>
          <TopFeatureCard merged={merged[1]} almaText={almaText} />
        </Flex>
      ) : (
        <Flex
          justify="center"
          align="stretch"
          gap={{ base: 4, md: 5 }}
          flexWrap="wrap"
        >
          {merged.map((m) => (
            <TopFeatureCard key={m.id} merged={m} almaText={almaText} />
          ))}
        </Flex>
      )}
    </Box>
  );
}

interface CategoryBlockProps {
  category: PricingCategory;
  liveById: Map<string, Service>;
  globalTexts: PricingGlobalTexts;
  almaText: string;
  hideHeader?: boolean;
}

function CategoryBlock({ category, liveById, globalTexts, almaText, hideHeader }: CategoryBlockProps) {
  const merged = useMemo(() => {
    return [...category.services]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((svc) => mergePricingService(svc, category, globalTexts, liveById.get(String(svc.mindbodyId))))
      .filter((m): m is NonNullable<typeof m> => m !== null);
  }, [category, globalTexts, liveById]);

  if (merged.length === 0) return null;

  return (
    <Box className="categoryBlock">
      {!hideHeader && (
        <Stack spacing={2} mb={5} textAlign="center" className="categoryHeader">
          <Heading as="h2" size="lg" color="gray.800" className="categoryTitle">
            {category.label}
          </Heading>
          {category.introText && (
            <Text fontSize="sm" color="gray.600" maxW="700px" mx="auto" className="categoryIntro">
              {category.introText}
            </Text>
          )}
        </Stack>
      )}

      <Flex className="categoryGrid" flexWrap="wrap" justify="center" gap={{ base: 4, md: 6 }} align="flex-start">
        {merged.map((m) => (
          <Box
            key={m.id}
            flex={{ base: '0 1 100%', md: '0 1 calc(50% - 12px)', lg: '0 1 calc(33.333% - 16px)' }}
            maxW={{ base: '320px', md: 'none' }}
            w="full"
            minW={0}
          >
            <PackCard merged={m} almaText={almaText} />
          </Box>
        ))}
      </Flex>

      {merged.some((m) => m.almaEnabled) && (
        <AlmaMention variant="footer" text={globalTexts?.almaFooterText ?? ''} />
      )}
    </Box>
  );
}

interface ContractsBlockProps {
  contracts: PricingContract[];
  liveContractById: Map<string, MindbodyContract>;
  liveById: Map<string, Service>;
  globalTexts: PricingGlobalTexts;
  /** Optional heading shown above the grid (used by FlatLayout, omitted in NavigationLayout). */
  heading?: string;
  /** Cartes par ligne sur grand écran. 3 = comportement historique. */
  columns?: PricingContractsColumns;
}

function ContractsBlock({ contracts, liveContractById, liveById, globalTexts, heading, columns }: ContractsBlockProps) {
  const merged = useMemo(() => {
    return [...contracts]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((c) => mergePricingContract(c, globalTexts, liveContractById.get(String(c.mindbodyContractId)), liveById))
      .filter((m): m is NonNullable<typeof m> => m !== null);
  }, [contracts, globalTexts, liveContractById, liveById]);

  if (merged.length === 0) return null;

  // Le `gap` desktop vaut 24px : on retire (n-1)/n * 24 à chaque carte pour
  // que n cartes tiennent exactement sur une ligne.
  const cols = columns ?? 3;
  const lgBasis = `0 1 calc(${(100 / cols).toFixed(3)}% - ${Math.round(((cols - 1) / cols) * 24)}px)`;

  return (
    <Box mt={heading ? { base: 8, md: 12 } : 0} className="contractsBlock">
      {heading && (
        <Stack spacing={2} mb={5} textAlign="center" className="contractsHeader">
          <Heading as="h2" size="lg" color="gray.800" className="contractsTitle">
            {heading}
          </Heading>
        </Stack>
      )}

      <Flex
        className="contractsGrid"
        flexWrap="wrap"
        justify="center"
        gap={{ base: 4, md: 6 }}
        align="flex-start"
        // Voir CARD_MAX_WIDTH : on borne la grille à la largeur réellement
        // occupée par les cartes (2 colonnes en md, `cols` en lg) pour qu'elle
        // reste centrée. Au-delà de ~4 colonnes la borne dépasse le container
        // et n'a plus d'effet — les cartes remplissent alors leur colonne.
        maxW={{ base: 'none', md: gridMaxWidth(2), lg: gridMaxWidth(cols) }}
        mx="auto"
      >
        {merged.map((m) => (
          <Box
            key={m.id}
            flex={{ base: '0 1 100%', md: '0 1 calc(50% - 12px)', lg: lgBasis }}
            maxW={{ base: '320px', md: 'none' }}
            w="full"
            minW={0}
          >
            <ContractCard merged={m} />
          </Box>
        ))}
      </Flex>
    </Box>
  );
}
